import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Cell,
  Column,
  type ColumnProps,
  Row,
  type RowProps,
  Table as AriaTable,
  TableBody,
  TableHeader,
  type TableProps as AriaTableProps,
} from 'react-aria-components';
import LoadingIcon from '@components/sds/icons/loading';
import SortIcon from '@components/sds/icons/sort-icon';
import { createStyleGetter, formatCellValue } from './table-utils.js';
import styles from './table.module.scss';

/**
 * Validates that a container element exists.
 * @throws {Error} If element is null
 */
export const validateContainerElement = (
  element: HTMLDivElement | null
): HTMLDivElement => {
  if (!element) {
    throw new Error('Expected table wrapper element.');
  }

  return element;
};

/**
 * Validates that a table header element exists.
 * @throws {Error} If element is null
 */
export const validateTheadElement = (
  thead: HTMLElement | null
): HTMLElement => {
  if (!thead) {
    throw new Error('Expected table header element.');
  }

  return thead;
};

/**
 * Validates that a row exists for the given key.
 * @throws {Error} If row is undefined
 */
export const validateRowExists = <TRow,>(
  row: TRow | undefined,
  key: React.Key
): TRow => {
  if (!row) {
    throw new Error(`Expected to find row with key ${String(key)}.`);
  }

  return row;
};

/**
 * Creates a ResizeObserver if available, or returns undefined.
 * This allows testing the fallback when ResizeObserver is not available.
 */
export const createResizeObserverIfAvailable = (
  callback: ResizeObserverCallback
): ResizeObserver | undefined => {
  return typeof ResizeObserver === 'undefined'
    ? undefined
    : new ResizeObserver(callback);
};

/**
 * Safely gets the offsetWidth from an element if it's an HTMLElement.
 * Returns 0 if the element is not an HTMLElement.
 */
export const getOffsetWidth = (element: Element | undefined): number => {
  return element instanceof HTMLElement ? element.offsetWidth : 0;
};

/**
 * Converts an Element to HTMLElement or null.
 * Returns the element if it's an HTMLElement, otherwise null.
 */
export const toHTMLElementOrNull = (
  element: Element | null | undefined
): HTMLElement | null => {
  return element instanceof HTMLElement ? element : null;
};

/**
 * Gets the style object for the top shadow when using sticky headers.
 * Returns a style object with top position if both sticky header is enabled and height > 0.
 * Returns undefined otherwise.
 */
export const getTopShadowStyle = (
  stickyHeader: boolean,
  headerHeight: number
): React.CSSProperties | undefined => {
  return stickyHeader && headerHeight > 0
    ? { top: `${headerHeight}px` }
    : undefined;
};

/**
 * Helper to safely access CSS module classes.
 *
 * Note: We use standard CSS modules rather than `useInlineStyles` because
 * useInlineStyles is designed for runtime style injection and conditional classes,
 * whereas standard CSS modules are simpler and already optimized by Vite.
 */
const getStyle = createStyleGetter(styles);

export interface ColumnDefinition<TRow> {
  align?: 'left' | 'right';
  field?: string;
  header?: string;
  render?: (value: string, row: TRow) => ReactNode;
  sortable?: boolean;
  sticky?: 'left' | 'right';
  valueFormatter?: (value: unknown) => string;
  valueGetter?: (row: TRow) => unknown;
  wrapHeader?: boolean;
  wrapValue?: boolean;
}

export interface SortDescriptor {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface TableProperties<TRow> {
  activeRowId?: React.Key;
  'aria-label'?: string;
  columns: ColumnDefinition<TRow>[];
  data: TRow[];
  density?: 'relaxed' | 'regular' | 'condensed';
  emptyStateMessage?: string;
  getRowHref?: (row: TRow) => string | undefined;
  getRowId?: (row: TRow) => React.Key;
  isLoading?: boolean;
  onRowClick?: (row: TRow) => void;
  onSort?: (sort: SortDescriptor) => void;
  sortDescriptor?: SortDescriptor;
  stickyHeader?: boolean;
}

export const Table = <TRow extends object>({
  activeRowId,
  'aria-label': ariaLabel,
  columns,
  data,
  density = 'regular',
  emptyStateMessage = 'No data to show.',
  getRowHref,
  getRowId,
  isLoading = false,
  onRowClick,
  onSort,
  sortDescriptor,
  stickyHeader = false,
}: TableProperties<TRow>): ReactElement => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [stickyOffsets, setStickyOffsets] = useState<{
    left: number[];
    right: number[];
  }>({ left: [], right: [] });
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Check for horizontal overflow and scroll position
  useEffect(() => {
    // Capture ref for cleanup and validate it exists
    const containerElement = validateContainerElement(tableRef.current);

    const checkScroll = () => {
      const {
        clientHeight,
        clientWidth,
        scrollHeight,
        scrollLeft,
        scrollTop,
        scrollWidth,
      } = containerElement;
      const hasHorizontalOverflow = scrollWidth > clientWidth;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

      // Show left shadow (on left sticky columns) when content is hidden to the left
      setShowLeftShadow(hasHorizontalOverflow && canScrollLeft);
      // Show right shadow (on right sticky columns) when content is hidden to the right
      setShowRightShadow(hasHorizontalOverflow && canScrollRight);

      const hasVerticalOverflow = scrollHeight > clientHeight;
      const canScrollUp = scrollTop > 0;
      const canScrollDown = scrollTop < scrollHeight - clientHeight - 1;

      // Show top shadow when content is hidden above
      setShowTopShadow(hasVerticalOverflow && canScrollUp);
      // Show bottom shadow when content is hidden below
      setShowBottomShadow(hasVerticalOverflow && canScrollDown);
    };

    // Check initially and on resize
    checkScroll();

    // Only use ResizeObserver if available (not in test environments)
    const resizeObserver = createResizeObserverIfAvailable(checkScroll);
    resizeObserver?.observe(containerElement);

    // Check on scroll
    containerElement.addEventListener('scroll', checkScroll);

    return () => {
      resizeObserver?.disconnect();
      containerElement.removeEventListener('scroll', checkScroll);
    };
  }, [data, columns]);

  // Calculate sticky column offsets and header height
  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      const headerCells = tableRef.current?.querySelectorAll('thead th');

      if (!headerCells) {
        throw new Error('Expected table header cells.');
      }

      const leftOffsets: number[] = [];
      const rightOffsets: number[] = [];
      let leftAccumulated = 0;
      let rightAccumulated = 0;

      // Calculate left sticky offsets (left to right)
      columns.forEach((col, index) => {
        if (col.sticky === 'left') {
          leftOffsets[index] = leftAccumulated;
          const headerCell = headerCells[index];
          leftAccumulated += getOffsetWidth(headerCell);
        }
      });

      // Calculate right sticky offsets (right to left)
      for (let idx = columns.length - 1; idx >= 0; idx--) {
        const col = columns[idx];

        if (col?.sticky === 'right') {
          rightOffsets[idx] = rightAccumulated;
          const headerCell = headerCells[idx];
          rightAccumulated += getOffsetWidth(headerCell);
        }
      }

      setStickyOffsets({ left: leftOffsets, right: rightOffsets });

      // Calculate header height for top shadow positioning
      if (stickyHeader) {
        const theadElement = tableRef.current?.querySelector('thead');
        const thead = validateTheadElement(toHTMLElementOrNull(theadElement));
        setHeaderHeight(thead.offsetHeight);
      }
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [columns, data, stickyHeader]);

  const defaultGetRowId = (row: TRow): React.Key => {
    const rowWithId = row as { id?: React.Key };

    return rowWithId.id ?? data.indexOf(row);
  };

  const resolvedGetRowId = getRowId ?? defaultGetRowId;

  const handleRowAction = (key: React.Key) => {
    const row = data.find(dataRow => resolvedGetRowId(dataRow) === key);
    const validatedRow = validateRowExists(row, key);
    onRowClick?.(validatedRow);
  };

  const handleSortChange = (descriptor: {
    column: React.Key;
    direction: 'ascending' | 'descending';
  }) => {
    // Find the column by ID to get its field property
    const columnId = String(descriptor.column);
    const column = columns.find((col, idx) => {
      const id = col.field ?? `column-${idx}`;

      return id === columnId;
    });

    // Only fire onSort if the column has a field property
    if (column?.field && onSort) {
      onSort({
        column: column.field,
        direction: descriptor.direction === 'ascending' ? 'ASC' : 'DESC',
      });
    }
  };

  // Find the index of the last left-sticky and first right-sticky columns
  const lastLeftStickyIndex = columns.reduce(
    (lastIndex, col, index) => (col.sticky === 'left' ? index : lastIndex),
    -1
  );
  const firstRightStickyIndex = columns.findIndex(
    col => col.sticky === 'right'
  );

  const getStickyClasses = (
    column: ColumnDefinition<TRow>,
    index: number
  ): string[] => {
    const classes: string[] = [];

    if (column.sticky === 'left') {
      classes.push(getStyle('stickyLeft'));

      if (index === lastLeftStickyIndex && showLeftShadow) {
        classes.push(getStyle('stickyInner'));
      }
    } else if (column.sticky === 'right') {
      classes.push(getStyle('stickyRight'));

      if (index === firstRightStickyIndex && showRightShadow) {
        classes.push(getStyle('stickyInner'));
      }
    }

    return classes;
  };

  const getHeaderClassName = (
    column: ColumnDefinition<TRow>,
    index: number
  ) => {
    const classes: string[] = [
      getStyle('header'),
      ...getStickyClasses(column, index),
    ];

    if (column.wrapHeader) {
      classes.push(getStyle('wrap'));
    }

    if (column.align === 'right') {
      classes.push(getStyle('alignRight'));
    }

    if (column.sortable) {
      classes.push(getStyle('sortable'));
    }

    return classes.join(' ');
  };

  const getCellClassName = (column: ColumnDefinition<TRow>, index: number) => {
    const classes: string[] = [
      getStyle('cell'),
      ...getStickyClasses(column, index),
    ];

    if (column.wrapValue) {
      classes.push(getStyle('wrap'));
    }

    if (column.align === 'right') {
      classes.push(getStyle('alignRight'));
    }

    return classes.join(' ');
  };

  const getStickyStyle = (
    column: ColumnDefinition<TRow>,
    index: number
  ): React.CSSProperties | undefined => {
    if (!column.sticky) {
      return undefined;
    }

    if (column.sticky === 'left') {
      const offset = stickyOffsets.left[index];

      return { left: `${typeof offset === 'number' ? offset : 0}px` };
    }

    // column.sticky must be 'right' at this point
    const offset = stickyOffsets.right[index];

    return { right: `${typeof offset === 'number' ? offset : 0}px` };
  };

  // Map our sortDescriptor format to RAC's format
  const ariaSortDescriptor:
    | { column: string; direction: 'ascending' | 'descending' }
    | undefined = sortDescriptor
    ? {
        column: sortDescriptor.column,
        direction:
          sortDescriptor.direction === 'ASC' ? 'ascending' : 'descending',
      }
    : undefined;

  const ariaTableProps: Partial<AriaTableProps> = {
    'aria-label': ariaLabel ?? 'Data table',
    onRowAction: onRowClick ? handleRowAction : undefined,
    onSortChange: onSort ? handleSortChange : undefined,
    sortDescriptor: ariaSortDescriptor,
  };

  const getDensityClassName = () => {
    const classes = [getStyle('table')];

    if (density === 'relaxed') {
      classes.push(getStyle('densityRelaxed'));
    } else if (density === 'condensed') {
      classes.push(getStyle('densityCondensed'));
    }

    return classes.join(' ');
  };

  const isEmpty = data.length === 0 && !isLoading;

  const getContainerClassName = () => {
    const classes = [getStyle('tableContainer')];

    if (isEmpty) {
      classes.push(getStyle('tableContainerEmpty'));
    }

    return classes.join(' ');
  };

  return (
    <div className={getContainerClassName()}>
      {showTopShadow && (
        <div
          className={getStyle('topShadow')}
          style={getTopShadowStyle(stickyHeader, headerHeight)}
        />
      )}
      {showBottomShadow && <div className={getStyle('bottomShadow')} />}
      <div className={getStyle('tableWrapper')} ref={tableRef}>
        <AriaTable className={getDensityClassName()} {...ariaTableProps}>
          <TableHeader
            className={stickyHeader ? getStyle('stickyHeader') : undefined}
          >
            {columns.map((column, index) => {
              const stickyStyle = getStickyStyle(column, index);
              const columnProps: Partial<ColumnProps> & {
                style?: React.CSSProperties;
              } = {
                allowsSorting: column.sortable ?? false,
                className: getHeaderClassName(column, index),
                id: column.field ?? `column-${index}`,
                isRowHeader: index === 0,
                style: stickyStyle,
              };

              const isSorted = sortDescriptor?.column === column.field;
              const iconDirection = isSorted
                ? sortDescriptor?.direction
                : undefined;

              return (
                <Column
                  {...columnProps}
                  key={column.field ?? `column-${index}`}
                >
                  {column.sortable ? (
                    <span className={getStyle('sortableContent')}>
                      {column.header ?? column.field ?? ''}
                      <SortIcon aria-hidden="true" direction={iconDirection} />
                    </span>
                  ) : (
                    column.header ?? column.field ?? ''
                  )}
                </Column>
              );
            })}
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <Row className={getStyle('emptyRow')}>
                <Cell
                  className={getStyle('emptyCell')}
                  colSpan={columns.length}
                >
                  {emptyStateMessage}
                </Cell>
              </Row>
            ) : (
              data.map(row => {
                const rowKey = resolvedGetRowId(row);
                const href = getRowHref?.(row);
                const isClickable = Boolean(onRowClick ?? href);
                const isActive =
                  activeRowId !== undefined && rowKey === activeRowId;

                const rowClasses = [getStyle('row')];

                if (isClickable) {
                  rowClasses.push(getStyle('clickableRow'));
                }

                if (isActive) {
                  rowClasses.push(getStyle('activeRow'));
                }

                const rowClassName = rowClasses.join(' ');

                const rowProps: Partial<RowProps<TRow>> = {
                  className: rowClassName,
                  href,
                  id: String(rowKey),
                };

                return (
                  <Row {...rowProps} key={rowKey}>
                    {columns.map((column, columnIndex) => {
                      const formattedValue = formatCellValue(row, column);
                      const stickyStyle = getStickyStyle(column, columnIndex);

                      return (
                        <Cell
                          key={column.field ?? `cell-${columnIndex}`}
                          className={getCellClassName(column, columnIndex)}
                          style={stickyStyle}
                        >
                          {column.render
                            ? column.render(formattedValue, row)
                            : formattedValue}
                        </Cell>
                      );
                    })}
                  </Row>
                );
              })
            )}
          </TableBody>
        </AriaTable>
      </div>
      {isLoading && (
        <div
          aria-label="Loading"
          aria-live="polite"
          className={getStyle('loadingOverlay')}
          role="status"
        >
          <LoadingIcon aria-hidden="true" width="32" height="32" />
        </div>
      )}
    </div>
  );
};
